import React, { useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Table, Form, Input, Typography, Tooltip, Popover, Button, message, Modal } from 'antd';
import AuditTrail from '../AuditTrail';
import { fetchAccountByNumber } from '../../utils/accountStructure/accountStructureUtils';
import ResizableTitle from '../ResizeableTitle';
import ColumnSettings from '../ColumnSettings';
import TruncatedText from '../TruncatedText';
import RowContextMenu from '../RowContextMenu';
import { useSharedRowMenuItems } from '../../hooks/useSharedRowMenuItems';
import { useTableKeyboardNav } from '../../hooks/useTableKeyboardNav';
import { useHiddenColumns } from '../../hooks/useHiddenColumns';
import { useLocateRow, LOCATE_HIT_CLASS } from '../../hooks/useLocateRow';
import { ScenarioTableContext } from './ScenarioTableContext';
import { getVisiblePeriods } from '../../utils/periods';

import './styles.scss';

const { Text } = Typography;

message.config({
  top: window.innerHeight / 2 - 50, // roughly vertical center
  duration: 1.5,
});

const NUMERIC = "NUMERIC";
const RATIO = "RATIO";
const UNIT = "UNIT";

const ROW_H = 17;      // measure a real <tr> in devtools
const HEADER_H = 40;
const SCROLLBAR_H = 16;


const SEPARATOR_TYPES = Object.freeze({
    SINGLE: 'single',
    DOUBLE: 'double',
    BLANK: 'blank',
	TEXT: 'text',
});

const isAccountSeparator = (row) =>
    row?.row_type === 'separator' ||
    row?.separator_id != null;

const getSeparatorRowClassName = (row) => {
    if (!isAccountSeparator(row)) {
        return '';
    }

    switch (row.separator_type) {
        case SEPARATOR_TYPES.DOUBLE:
            return 'account-separator-row account-separator-double';

        case SEPARATOR_TYPES.BLANK:
            return 'account-separator-row account-separator-blank';

        case SEPARATOR_TYPES.SINGLE:
        default:
            return 'account-separator-row account-separator-single';
    }
};

const OTHER_ROW_TYPE = 'other';

const isOtherRow = (row) => row?.row_type === OTHER_ROW_TYPE;

// A custom-tab row whose account has sub-accounts that are not on the tab.
const isPartialRollUp = (row) =>
    row?.custom_tab_accounts_id != null &&
    row.hidden_children_count > 0 &&
    row.children?.length > 0 &&
    row.account?.numeric === true;

const EditableContext = React.createContext(null);

const EditableRow = ({ index, ...rowProps }) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...rowProps} />
            </EditableContext.Provider>
        </Form>
    );
};


function getRowAccountId(row) {
  const id =
    row?.account?.account_id ??
    row?.account_id ??
    row?.id ??
    row?.account?.id;
  const num = Number(id);
  return Number.isFinite(num) ? num : undefined;
}

function areCellPropsEqual(prev, next) {

	 if (!prev.dataIndex?.startsWith('period') || !next.dataIndex?.startsWith('period')) {
    return false;
  }

	if (prev.className !== next.className) return false;

	if (prev.style?.left !== next.style?.left) return false;
	if (prev.style?.right !== next.style?.right) return false;
	if (prev.style?.position !== next.style?.position) return false;
	if (prev.style?.zIndex !== next.style?.zIndex) return false;
	if (prev.style?.boxShadow !== next.style?.boxShadow) return false;

    if (prev.editable !== next.editable) return false;
    if (prev.dataIndex !== next.dataIndex) return false;
    if (prev.account !== next.account) return false;
    if (prev.rawInput !== next.rawInput) return false;
    if (prev.valueId !== next.valueId) return false;
    if (prev.isFormulaDriven !== next.isFormulaDriven) return false;
    if (prev.pound !== next.pound) return false;
    if (prev.className !== next.className) return false;
    if (prev.displayValue !== next.displayValue) return false;
    if (prev.isNA !== next.isNA) return false;
    if (prev.isParentYearForecast !== next.isParentYearForecast) return false;
    if (prev.isManual !== next.isManual) return false;
    if (prev.isParent !== next.isParent) return false;
    return true;
    }
	
	function formatLocalNumericInput(raw, organization) {
		const trimmed = String(raw ?? '').trim();
		if (trimmed === '' || trimmed === '#V' || trimmed === '#F') return trimmed;

		const locale = organization?.notation === 'European' ? 'da-DK' : 'en-US';
		const decimals = organization?.numericDecimals ?? 2;

		let normalized = trimmed;
		if (locale === 'da-DK') {
			normalized = normalized.replace(/\./g, '').replace(',', '.');
		} else {
			normalized = normalized.replace(/,/g, '');
		}

		if (!/^-?\d+(\.\d+)?$/.test(normalized)) return trimmed;

		const num = Number(normalized);
		if (!Number.isFinite(num)) return trimmed;

		return num.toLocaleString(locale, {
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		});
		}
		
		const EditableCell = React.memo(({
			title,
			editable,
			children,
			dataIndex,
			account,
			handleSave,
			testing,
			...restProps
			}) => {
				const [editing, setEditing] = useState(false);
				const [localValue, setLocalValue] = useState(null);
				const inputRef = useRef(null);
				const form = useContext(EditableContext);

				const manualFocus = useRef(false);
				const skipNextBlur = useRef(false);

				const ctx = useContext(ScenarioTableContext) ?? {};

				const mountedRef = useRef(true);
				const timeoutsRef = useRef([]);

				useEffect(() => {
				return () => {
					mountedRef.current = false;
					timeoutsRef.current.forEach(clearTimeout);
				};
				}, []);

				const safeSetTimeout = (fn, delay = 0) => {
				const id = setTimeout(() => {
					if (mountedRef.current) fn();
				}, delay);

				timeoutsRef.current.push(id);
				return id;
				};

				

				const {
					handleSetAuditTrailData,
					isBCView,
					onOpenAccountManagement,
					editedCellIds,
					cellHasColor,
					organization,
					openContextMenu,
					} = ctx; //useContext(ScenarioTableContext);

				useEffect(() => {
					setLocalValue(null);
					}, [
					restProps.displayValue,
					organization?.numericDecimals,
					organization?.ratioDecimals,
					organization?.unitDecimals,
					organization?.notation,
					organization?.amountSizing,
				]);
			


			useEffect(() => {
			if (editing && !manualFocus.current) {
				inputRef.current?.focus();
			}

			manualFocus.current = false;
			}, [editing]);

			if (account && account.children && account.children.length) {
				editable = false;
			}

			const cellContext = dataIndex?.startsWith('period')
					? { period: dataIndex.slice(6), dataIndex }
					: null;

				
				const baseInput = restProps.rawInput ?? '';
				const rawInput = localValue ?? baseInput;
				const valueId = restProps.valueId;
				const isFormulaDriven = restProps.isFormulaDriven === true;

				const isOverridden = isFormulaDriven && restProps.pound === true;

				const isEdited = valueId != null && editedCellIds?.has(valueId);

				// When the cell stops being an uncalculated edit (calculated or undone),
				// drop the typed value. Undo can restore the same displayValue the cell
				// had before the edit, so the displayValue effect above would not fire.
				useEffect(() => {
					if (!isEdited) setLocalValue(null);
				}, [isEdited]);

				const displayValue = restProps.displayValue;
				const isNA = restProps.isNA === true;
				const isParentYearForecast = restProps.isParentYearForecast === true;
				const isManual = restProps.isManual === true;
				const isParent = restProps.isParent === true;

				const shownValue = localValue != null
					? localValue
					: (isNA ? 'NaN' : displayValue);

				const isPeriodCell = dataIndex?.startsWith('period');

				const cellBgColor = (cellHasColor && !isParent && isManual && !isEdited) ? 'lightgray' : '';

				const effectiveChildren = isPeriodCell ? (
					<span
						style={{
							backgroundColor: cellBgColor,
							color: isParentYearForecast ? 'blue' : 'black',
							display: 'inline-block',
							width: '100%',
							textAlign: 'right',
							cursor: 'inherit',
							opacity: isFormulaDriven ? 0.8 : 1,
						}}
					>
					{localValue != null ? localValue : (isNA ? 'NaN' : displayValue)}
					</span>
				) : children;


				const toggleEdit = () => {
				if (!editable) return;

				if (isFormulaDriven && !isOverridden) {
					skipNextBlur.current = true;
					Modal.confirm({
					title: 'Override formula?',
					content: 'This cell is formula-driven. Entering a value will override the formula. Type #F to revert back.',
					okText: 'Edit anyway',
					cancelText: 'Cancel',
					onOk: () => {
						
					manualFocus.current = true;
					setEditing(true);
					safeSetTimeout(() => {
						form.setFieldsValue({ [dataIndex]: rawInput });
						inputRef.current?.focus();
					}, 0);
				},
					});
					return;
				}

			const next = !editing;
			setEditing(next);
			if (next) {
				form.setFieldsValue({ [dataIndex]: rawInput });
			}
			};

			const save = async () => {

				if (skipNextBlur.current) {
					skipNextBlur.current = false;
					return;
				}
				try {
				const values = await form.validateFields();
				
				if (!mountedRef.current) return;

				const raw = values[dataIndex];

				if (raw === undefined) {
					setEditing(false);
					return;
				}

				setEditing(false);
				setLocalValue(formatLocalNumericInput(raw, organization)); 
				handleSave({
					account,
					dataIndex,
					value: raw,
				});
				} catch (errInfo) {}
			};

			let childNode;

			if (editing) {
				childNode = (
				<Form.Item
					style={{ margin: 0, backgroundColor: 'black' }}
					name={dataIndex}
					rules={[
					{
						required: false,
						message: `${title} is required.`,
					},
					]}
				>
					<Input
					ref={inputRef}
						onFocus={(e) => e.target.select()} 
						onPressEnter={save}
						onBlur={save}
						onKeyDown={(e) => {
							if (e.key === 'Escape') {
							e.stopPropagation();
							skipNextBlur.current = true;
							setEditing(false);
							const td = e.target.closest('td');
							safeSetTimeout(() => td?.focus(), 0);
							}
						}}
					/>
				</Form.Item>
				);
			} else {
				childNode = (
				<div
					className="editable-cell-value-wrap"
					style={{
					padding: 0,
					color: 'black',
					backgroundColor: isEdited ? '#d98b0d' : '#fff',
					cursor: editable && !isFormulaDriven ? 'pointer' : 'default',
					}}
					
					onClick={(e) => {
					
					e.currentTarget.closest('td')?.focus();
					}}
					onDoubleClick={toggleEdit}
				>
					{effectiveChildren}
				</div>
				);
			}

			const {
				rawInput: _rawInput,
				valueId: _valueId,
				isFormulaDriven: _isFormulaDriven,
				pound: _pound,
				displayValue: _displayValue,
				isNA: _isNA,
				isParentYearForecast: _isPYF,
				isManual: _isManual,
				isParent: _isParent,
				...tdProps
			} = restProps;

			if (isAccountSeparator(account)) {
				const isTextCell =
					account.separator_type ===
						SEPARATOR_TYPES.TEXT &&
					dataIndex === 'name';

				return (
					<td
						{...tdProps}
						className={[
							tdProps.className,
							'account-separator-cell',
							isTextCell
								? 'account-separator-text-cell'
								: '',
						]
							.filter(Boolean)
							.join(' ')}
					>
						{isTextCell ? (
							<span className="account-separator-text-label">
								{account.separator_text}
							</span>
						) : null}
					</td>
				);
			}

			return (
			<td
			{...tdProps}
			onContextMenu={(e) => {
				e.preventDefault();
				if (isOtherRow(account)) return;
				openContextMenu?.({
					account,
					cellContext,
					x: e.clientX,
  					y: e.clientY,
				});
			}}
			style={{
				...tdProps.style,
				cursor: editable && !isFormulaDriven ? 'pointer' : 'default',
			}}
			>
				{childNode}
				</td>
				);
			}, areCellPropsEqual);



const ScenarioTable = (props) => {
	const {
    columnWidths: widths = { account_number: 90, name: 150 },
    setColumnWidth: setWidth = () => {},
  	columnDefaults = { account_number: 90, name: 150 },
  } = props;
	const periods = useSelector((state) => state.periods.periods);
	const expandedPeriods = useSelector((state) => state.periods.expandedPeriods);
	const organization = useSelector((state) => state.organization.organization);
	const [isAuditTrailOpen, setIsAuditTrailOpen] = useState(false);
	const [auditTrailData, setAuditTrailData] = useState(null);

	const [expandedRowKeys, setExpandedRowKeys] = useState([]);

	const [contextMenu, setContextMenu] = useState({
			open: false,
			account: null,
			cellContext: null,
			x: 0,
 			y: 0,
		});

	const [headerMenu, setHeaderMenu] = useState({
		open: false,
		columnKey: null,
		x: 0,
		y: 0,
	});

	/* Hidden columns persist per organization in localStorage. */
	const orgId = organization.organization_id;

	const { hidden, hideColumn, showColumn, showAll } =
		useHiddenColumns(`scenarioTable.hiddenColumns:${orgId}`);

	const normalizeFormula = (formula) => String(formula ?? '').trim();

	const isNumericLiteral = (formula) => {
	const f = normalizeFormula(formula);
	if (f === '') return false;
	return !Number.isNaN(Number(f));
	};

	const isExpressionFormula = (formula) => {
	const f = normalizeFormula(formula);
	if (f === '') return false;
	if (isNumericLiteral(f)) return false;

	// account references or expression syntax
	return /\{[^}]+\}/.test(f) || /[+\-*/()]/.test(f);
	};

	const getPeriodStructuralFormula = (scenarioAccount, periodId) => {
	if (!Array.isArray(scenarioAccount?.formulas)) return '0';
	const hit = scenarioAccount.formulas.find((x) => x?.period === periodId);
	return hit?.formula ?? '0';
	};

	const isCellFormulaDriven = (scenarioAccount, rawValue, periodId) => {
	const structuralFormula = getPeriodStructuralFormula(scenarioAccount, periodId);
	const valueFormula = rawValue?.formula ?? '0';

	// structural formula should be the main source of truth,
	// but value formula can also lock if an expression somehow ends up there
	return isExpressionFormula(structuralFormula) || isExpressionFormula(valueFormula);
	};
	
	const { wrapperRef } = useTableKeyboardNav({
		onEnterEdit: (_r, _c, td) => {
			const wrap = td.querySelector('.editable-cell-value-wrap');
			wrap?.dispatchEvent(new MouseEvent('dblclick', { bubbles: true }));
		},
	});


	let formattingLocale = undefined;
	if(organization?.notation === "European")
		formattingLocale = "da-DK";
	else if(organization?.notation === "American")
		formattingLocale = "en-US";

	let divisor;
    switch(organization?.amountSizing) {
        case 'Units':
            divisor = 1;
            break;
        case 'Thousands':
            divisor = 1_000;
            break;
        case 'HundredThousands':
            divisor = 100_000;
            break;
        case 'Millions':
            divisor = 1_000_000;
            break;
        default:
            divisor = 1; // Default to units if no valid option is provided
    }


	const handleSetAuditTrailData = (accountNumber, period, context) => {
	const wrapper = {};
	fetchAccountByNumber(accountNumber, wrapper, props.structure);
	if (!wrapper.account) return;

	const frame = { account: wrapper.account, period };
	if (context) frame.context = context;

	setAuditTrailData((prev) => (Array.isArray(prev) ? [...prev, frame] : [frame]));
	setIsAuditTrailOpen(true);
	};

	const handleSetPreviousAuditTrailData = () => {
	if (!Array.isArray(auditTrailData) || auditTrailData.length <= 1) return;
	setAuditTrailData(auditTrailData.slice(0, -1));
	};


	const columnsInitial = useMemo(() => [
		{
			title: 'Account',
			dataIndex: 'account_number',
			key: 'account_number',
			fixed: 'left',
			width: widths.account_number,        
			ellipsis: { showTitle: false },
			render: (value, account) => {
			const rowId = getRowAccountId(account);
			const isTargetRow = rowId != null && props.targetAccountSet?.has(rowId);
			const accountNumber = account.account ? account.account.account_number : value;
			return (
				<TruncatedText textProps={{ style: { opacity: isTargetRow ? 0.6 : 1 } }}>
				{accountNumber}
				</TruncatedText>
			);
			},
		},
		{
			title: 'Name',                      
			dataIndex: 'name',
			key: 'name',
			fixed: 'left',
			width: widths.name,                  
			ellipsis: { showTitle: false },
			render: (value, account) => {
			const rowId = getRowAccountId(account);
			const isTargetRow = rowId != null && props.targetAccountSet?.has(rowId);
			const name = account.account ? account.account.name : value;
			if (isOtherRow(account)) {
				return (
					<TruncatedText textProps={{ type: 'secondary', italic: true }}>
					{name}
					</TruncatedText>
				);
			}
			return (
				<TruncatedText textProps={{ style: { opacity: isTargetRow ? 0.6 : 1 } }}>
				{name}
				</TruncatedText>
					);
				},
			},
		], [widths, setWidth, columnDefaults, props.targetAccountSet]);
		
		const resizableInitial = useMemo(() =>
			 columnsInitial.map((col) => {
				
				if (typeof col.width !== 'number') return col;
				return {
					...col,
					onHeaderCell: (column) => ({
					width: column.width,
					onResize: (_, { size }) => {
						const next = Math.max(60, size.width);
						setWidth(col.key, next);
					},
				}),
			};
		}),
		[columnsInitial, setWidth]
	);
	
	const getNumeric = (sv, isCompiled) => {
		if (!sv) return 0;
		
		// compiled: prefer numeric result
		if (isCompiled && typeof sv.result === 'number') return sv.result;

		// fallback: parse formula string
		const n = Number(sv.formula);
		return Number.isFinite(n) ? n : 0;
	};


	const formatValue = (scenarioValue, valueType, isCompiled) => {
		let cellValue = '0.00';
		if(scenarioValue !== undefined) {
			 // special: non-numeric formula strings (only relevant in non-compiled view)
		if (!isCompiled && valueType === NUMERIC && isNaN(+scenarioValue.formula)) {
		return scenarioValue.formula;
		}

    	const n = getNumeric(scenarioValue, isCompiled);

		const numericDecimals = organization?.numericDecimals ?? 2;
		const ratioDecimals = organization?.ratioDecimals ?? 2;
		const unitDecimals = organization?.unitDecimals ?? 0;

		if (valueType === NUMERIC) {
		cellValue = (n / divisor).toLocaleString(formattingLocale, {
			minimumFractionDigits: numericDecimals,
			maximumFractionDigits: numericDecimals,
		});
		} else if (valueType === RATIO) {
		cellValue = n.toLocaleString(formattingLocale, {
			minimumFractionDigits: ratioDecimals,
			maximumFractionDigits: ratioDecimals,
		});
		} else if (valueType === UNIT) {
		cellValue = n.toLocaleString(formattingLocale, {
			minimumFractionDigits: unitDecimals,
			maximumFractionDigits: unitDecimals,
		});
		} else {
		cellValue = n.toLocaleString(formattingLocale);
		}
	}

	return cellValue;
	};

	const periodColumn = (period, index, allPeriods = props.periods) => {
		const year = period.period.split('-')[0];
		const yearPeriod = allPeriods.find(p => p.period === year);
		const isParentYearForecast = yearPeriod?.forecast ?? false;
		

		return {
				title: (
					<span style={{ color: isParentYearForecast ? 'blue' : 'black', display: 'block' }}
					onContextMenu={(e) => {
						e.preventDefault();
						setHeaderMenu({ open: true, columnKey: period.period, x: e.clientX, y: e.clientY });
					}}
				>
					{period.period}
				</span>
			),
			dataIndex: 'period' + period.period,
			key: period.period,
			width: '100px',
			ellipsis: true,
			/*render: (_, account) => {
				const scenarioAccount = account.account !== undefined ? account.account : account;
				const isScenarioValue = !!props.scenario?.values?.has(scenarioAccount.account_number);
				const isParent = !!(account && account.children && account.children.length);
				const isCompiled = props.scenario?.compiled === true;

				if (props.scenario && props.scenario.values && isScenarioValue) {
					// English comma and 2 digits, i.e. 10,100.00
					const scenarioValues = props.scenario.values.get(
						scenarioAccount.account_number,
					);

					let valueIdx = 0;
						if (Array.isArray(scenarioValues)) {
						for (let i = 0; i < scenarioValues.length; i++) {
							if (scenarioValues[i]?.period === period.period) {
							valueIdx = i;
							break;
							}
						}
						}

						// Helper: extract {accountNumber} safely from a formula string
						const extractAssociatedAccountNumber = (acct) => {
						const f = acct?.formulas?.[0]?.formula;
						if (typeof f !== 'string') return null;
						const m = f.match(/\{([^}]+)\}/);
						return m?.[1] ?? null;
						};

						// If this is an associated account, grab the valueType from it's parent 
					let valueTypeAccount = scenarioAccount;
    					if (scenarioAccount?.associated_account_id) {
						// TODO: This should be a property in the associated account to have the account_number of its association
						 const associatedAccountNumber = extractAssociatedAccountNumber(scenarioAccount);
						 
						 if (associatedAccountNumber) {
							const fetched = {};
							
							fetchAccountByNumber(associatedAccountNumber, fetched, props.structure);
							valueTypeAccount = fetched.account ?? valueTypeAccount;
						} else {
						// Optional: console.warn for visibility
						console.warn('No {account_number} found in formula for associated account', scenarioAccount);
					}
				}

					const valueType = valueTypeAccount.numeric ? NUMERIC : valueTypeAccount.ratio ? RATIO : UNIT;

					// Safely format the value (handle missing array or index)
					const rawValue = Array.isArray(scenarioValues) ? scenarioValues[valueIdx] : undefined;
					const isFormulaDriven = isCellFormulaDriven(scenarioAccount, rawValue, period.period);

					// if user edited this cell locally, prefer override
					const override =
					rawValue && props.cellOverrides?.has(rawValue.value_id)
						? props.cellOverrides.get(rawValue.value_id)
						: undefined;
					const hasOverride = override != null;
					const isManual = rawValue?.pound === true;

					// hasOverride: user is mid-edit locally, show their typed value instead of compiled result
					// pound=true: cell is manual (#V) but result is still currency-converted — use it for display
					const isCompiledForDisplay = isCompiled && !hasOverride;

					const effectiveFormula = hasOverride ? String(override) : (rawValue?.formula ?? '');
					const effectiveValueObj = rawValue ? { ...rawValue, formula: effectiveFormula } : undefined;

					const cellValue = effectiveValueObj
					? formatValue(effectiveValueObj, valueType, isCompiledForDisplay)
					: '0.00';

					const isNA = !!rawValue?.isNA;

					let cellBgColor = '';
					if (props.cellHasColor && !isParent && isManual) {
					cellBgColor = 'lightgray';
					}
        
					return (
						<span
						 	data-display={isNA ? 'NaN' : cellValue} 
							style={{
								backgroundColor:cellBgColor,
								color: isParentYearForecast ? 'blue' : 'black',
								display: 'inline-block',
								width: '100%',
								textAlign: 'right',
								cursor: 'inherit',
								opacity: isFormulaDriven ? 0.8 : 1,
							}}
						>
							{isNA ? 'NaN' : cellValue}
						</span>
					);
				} else {
					return null;
				}
			},*/
			editable: props.editable,
		};
	};

	const createColumns = (columnsInitial, periods, expandedPeriods, hiddenKeys) => {
		const newPeriods = getVisiblePeriods(periods, expandedPeriods);
	
		const all = [...columnsInitial, ...newPeriods.map((p, i) => periodColumn(p, i, periods))];

		const visible = all.filter((c) => !hiddenKeys.has(c.key));

		// flag the column that sits after a gap, so hidden columns are discoverable
			return visible.map((c, idx) => {
				const originalIdx = all.indexOf(c);
				const prevOriginalIdx = idx > 0 ? all.indexOf(visible[idx - 1]) : originalIdx;
				const hasGapBefore = idx > 0 && originalIdx - prevOriginalIdx > 1;

				return {
					...c,
					colIndex: idx,
					className: [c.className, hasGapBefore ? 'column-gap-before' : '']
						.filter(Boolean).join(' '),
				};
			});
		};

	const handleOpenAuditTrail = useCallback((e, account, period) => {
		e.preventDefault();
		
		const auditAccount = account.account ?? account;

		setAuditTrailData([{ account: auditAccount, period }]);
		setIsAuditTrailOpen(true);
	}, [setAuditTrailData, setIsAuditTrailOpen]);

	const handleFocus = (e) => e.target.select();
		
	const components = {
		body: {
			row: EditableRow,
			cell: EditableCell,
		},
	};

	function parseFormattedValue(formattedValue, organization, account) {
		// Determine the divisor based on the amount sizing
		let divisor = 1;
		if(account.numeric) {
			switch (organization?.amountSizing) {
				case 'Thousands':
					divisor = 1_000;
					break;
				case 'HundredThousands':
					divisor = 100_000;
					break;
				case 'Millions':
					divisor = 1_000_000;
					break;
				case 'Units':
				default:
					divisor = 1;
					break;
			}
		}
	
		// Determine the locale and corresponding number formatting symbols
		let locale = organization?.notation === "European" ? "da-DK" : "en-US";
		
		// Define a regular expression to remove locale-specific thousand separators
		let regex;
		if (locale === "da-DK") {
			// Danish locale uses "." as thousand separator and "," as decimal separator
			regex = /\./g;
		} else {
			// US locale uses "," as thousand separator and "." as decimal separator
			regex = /,/g;
		}
	
		// Remove the thousand separators
		let cleanValue = formattedValue.replace(regex, "");
		if (locale === "da-DK") {
			cleanValue = cleanValue.replace(",", "."); // Replace comma with dot for decimals
		}
	
		// Parse the cleaned value as a float
		let numericValue = parseFloat(cleanValue);

		// Convert back to the original value by multiplying with the divisor
		let originalValue = numericValue * divisor;
	
		return originalValue;
	}
	
	const handleSave = ({ account, dataIndex, value }) => {

		if (!dataIndex || !dataIndex.startsWith("period")) return;

		const rowId = getRowAccountId(account);
		const isTargetRow = rowId != null && props.targetAccountSet?.has(rowId);

		if (isTargetRow) {
			props.handleSetIsFromScenario?.(account);
			return;
		}

		// toggles (#F / #V)
		if (value === "#F" || value === "#V") {
			props.handleChange?.(account, value, dataIndex);
			return;
		}

		const scenarioAccount = account.account ?? account;

		// parse formatted numeric input
		const parsed = parseFormattedValue(String(value), organization, scenarioAccount);

		if (!Number.isFinite(parsed)) {
			return; // keep original value, do not mark dirty
			}

		props.handleChange?.(
			account,
			String(parsed),
			dataIndex
		);
		};
		
		/*
		 * Value of an "Other" row: the parent's total minus the sub-accounts shown
		 * under it, using each sub-account's roll-up ('S' subtracts, 'I' is ignored).
		 */
		const otherRowDisplayValue = (otherRow, periodId) => {
			const isCompiled = props.scenario?.compiled === true;

			const readNumber = (accountNumber) => {
				const sv = props.scenario?.values
					?.get?.(accountNumber)
					?.find?.((x) => x?.period === periodId);
				if (!sv || sv.isNA) return null;
				if (isCompiled && typeof sv.result === 'number') return sv.result;
				const n = Number(sv.formula);
				return Number.isFinite(n) ? n : null;
			};

			const parent = otherRow.otherOf;
			const total = readNumber(parent.account.account_number);
			if (total == null) return '';

			let shown = 0;
			for (const child of parent.children ?? []) {
				const childAccount = child.account ?? child;
				if (isAccountSeparator(child) || childAccount?.account_number == null) continue;

				const rollUp = props.scenario?.roll_up?.get?.(childAccount.account_number)?.[1];
				if (rollUp === 'I') continue;

				const value = readNumber(childAccount.account_number);
				if (value == null) return '';
				shown += rollUp === 'S' ? -value : value;
			}

			return ((total - shown) / divisor).toLocaleString(formattingLocale, {
				minimumFractionDigits: organization?.numericDecimals ?? 2,
				maximumFractionDigits: organization?.numericDecimals ?? 2,
			});
		};

		const editableColumns = useMemo(() => {
			return createColumns(resizableInitial, props.periods, props.expandedPeriods, hidden).map(
        (col) => {
			
			if (col.dataIndex?.startsWith('period')) {
			
			return {
				...col,
					onCell: (account, rowIndex) => {

					if (isOtherRow(account)) {
						const displayValue = otherRowDisplayValue(account, col.dataIndex.slice(6));
						return {
							account,
							editable: false,
							dataIndex: col.dataIndex,
							title: col.title,
							align: 'right',
							rawInput: displayValue,
							displayValue,
							isFormulaDriven: true,
							'data-cell-row': rowIndex,
							'data-cell-col': col.colIndex,
						};
					}

					if (isAccountSeparator(account)) {
						return {
							account,
							editable: false,
							dataIndex: col.dataIndex,
							title: col.title,
							className: 'account-separator-cell',
						};
					}

					const scenarioAccount =
						account.account !== undefined ? account.account : account;

					const periodId = col.dataIndex?.slice(6);

	
					const year = periodId.split('-')[0];
					const yearPeriod = props.periods?.find(p => p.period === year);
					const isParentYearForecast = yearPeriod?.forecast ?? false;

					let valueId = null;
					let rawValue = null;

					const isYearCell = /^\d{4}$/.test(periodId);

					const yearHasExpandedSubperiods = props.periods?.some((p, idx) => {
						if (!p?.period || p.period === periodId) return false;

						const sameYear = p.period.startsWith(`${periodId}-`);
						const isVisible =
						Array.isArray(props.expandedPeriods)
							? !!props.expandedPeriods[idx]
							: typeof props.expandedPeriods === "string"
							? !!props.expandedPeriods[idx]
							: false;

						return sameYear && isVisible;
					});

					const sumType = scenarioAccount?.sum_type;
					const isDerivedExpandedYear =
						isYearCell &&
						yearHasExpandedSubperiods &&
						(sumType === "sumperiods" || sumType === "lastperiod");

					const scenarioValues =
						props.scenario?.values?.get?.(scenarioAccount.account_number) ?? [];

					if (Array.isArray(scenarioValues)) {
						rawValue = scenarioValues.find((x) => x?.period === periodId) ?? null;
						valueId = rawValue?.value_id ?? null;
					}

					const rowId = getRowAccountId(account);
					const isTargetRow =
						rowId != null ? !!props.targetAccountSet?.has(rowId) : false;

					const isGroupRow = !!(account && account.children && account.children.length);

					const isFormulaDriven = isCellFormulaDriven(scenarioAccount, rawValue, periodId);

					const decimals = scenarioAccount?.numeric
						? organization?.numericDecimals ?? 2
						: scenarioAccount?.ratio
						? organization?.ratioDecimals ?? 2
						: organization?.unitDecimals ?? 0;

					const editDisplayValue = (() => {
						if (!rawValue) return '';

						

						const isCompiledForDisplay = props.scenario?.compiled === true;
						if (isCompiledForDisplay && typeof rawValue.result === 'number') {
							const n = scenarioAccount?.numeric
								? rawValue.result / divisor
								: rawValue.result;

								return n.toLocaleString(formattingLocale, {
								minimumFractionDigits: decimals,
								maximumFractionDigits: decimals,
								});
						}
						return rawValue.formula ?? '';
					})();

					return {
						account,
						editable:
						col.editable &&
						!isTargetRow &&
						!isGroupRow &&
						!isDerivedExpandedYear,
						dataIndex: col.dataIndex,
						title: col.title,
						align: 'right',
						valueId,
						rawInput: editDisplayValue, // override != null ? String(override) : (rawValue?.formula ?? ''),
						isFormulaDriven,
						pound: rawValue?.pound ?? false, 
						handleSave: (payload) => handleSave(payload),
						'data-cell-row': rowIndex,
						'data-cell-col': col.colIndex,
						tabIndex: 0,

						displayValue: rawValue?.isNA ? 'NaN' : editDisplayValue,
						isNA: !!rawValue?.isNA,
						isParentYearForecast,  // hoist this from periodColumn — see below
						isManual: rawValue?.pound === true,
						isParent: !!(account && account.children && account.children.length),
					};
				},
			};
		}

		


        // Name / account_number: route through EditableCell so the right-click
        // menu shows up, but stay non-editable (no inline editing on these).
        if (col.dataIndex === 'name' || col.dataIndex === 'account_number') {

            return {
                ...col,
                onCell: (account, rowIndex) => ({
                    account,
                    editable: false,
                    dataIndex: col.dataIndex,
                    title: col.title,
					 'data-cell-row': rowIndex,
					'data-cell-col': col.colIndex,
					tabIndex: 0,
                
				}),
				
            };
        }  
		
		return col;
		
    },
);

}, [
    resizableInitial,
    props.periods,
    props.expandedPeriods,
    props.scenario,
    props.targetAccountSet,
    props.editable,
    props.cellHasColor,
    divisor,
    formattingLocale,
	organization?.numericDecimals,
	organization?.ratioDecimals,
	organization?.unitDecimals,
	organization?.notation,
	organization?.amountSizing,
	hidden,
]);


const columnToggles = useMemo(() => {
     const fixed = [
        { key: 'account_number', label: 'Account', group: 'Account' },
        { key: 'name', label: 'Name', group: 'Account' },
    ];

    const periodToggles = getVisiblePeriods(props.periods, props.expandedPeriods).map((p) => {
        const year = p.period.split('-')[0];
        return {
            key: p.period,
            label: p.period === year ? 'Year total' : p.period.slice(year.length + 1),
            group: year,
        };
    });

    return [...fixed, ...periodToggles].map((c) => ({ ...c, visible: !hidden.has(c.key) }));
}, [props.periods, props.expandedPeriods, hidden]);

		const keyedAccounts = useMemo(() => {
		const walk = (nodes, prefix = 'r') =>
			(nodes || []).map((n, i) => {
			const own = n.account_id ?? n.account_number ?? n.separator_id ?? i;
			const key = `${prefix}:${i}:${own}`;
			const children = n.children?.length ? walk(n.children, key) : n.children;

			// A pinned parent showing only some of its sub-accounts gets an "Other"
			// line, so the visible lines add up to the parent's total.
			if (isPartialRollUp(n)) {
				children.push({
					row_type: OTHER_ROW_TYPE,
					otherOf: n,
					name: 'Other (not on this tab)',
					__rowKey: `${key}:other`,
				});
			}

			return {
				...n,
				__rowKey: key,
				children,
			};
			});
		return walk(props.accounts);
		}, [props.accounts]);

		const { highlightKey } = useLocateRow({
			focusRequest: props.focusRequest,
			rows: keyedAccounts,
			getKey: (row) => row.__rowKey,
			containerRef: wrapperRef,
			onExpand: (keys) =>
				setExpandedRowKeys((prev) => Array.from(new Set([...prev, ...keys]))),
		});

		const getRowClassName = useCallback(
			(row) =>
				row.__rowKey === highlightKey
					? `${getSeparatorRowClassName(row)} ${LOCATE_HIT_CLASS}`
					: getSeparatorRowClassName(row),
			[highlightKey],
		);

		const visibleRowCount = useMemo(() => {
		const expanded = new Set(expandedRowKeys);
		let n = 0;
		const walk = (nodes) => {
			(nodes || []).forEach((node) => {
			n += 1;
			if (node.children?.length && expanded.has(node.__rowKey)) walk(node.children);
			});
		};
		walk(keyedAccounts);
		return n;
		}, [keyedAccounts, expandedRowKeys]);

		const naturalHeight = visibleRowCount * ROW_H + HEADER_H + SCROLLBAR_H;
		const effectiveHeight = props.tableHeight
		? Math.min(naturalHeight, props.tableHeight)
		: undefined;	

const tableContextValue = useMemo(() => ({
    // From ScenarioTable's own state
    setAuditTrailData,
    setIsAuditTrailOpen,
    handleOpenAuditTrail,
	handleSetAuditTrailData,

    // From props (passed through)
    isBCView: props.isBCView,
    onOpenAccountManagement: props.onOpenAccountManagement,
    editedCellIds: props.editedCellIds,
	cellHasColor: props.cellHasColor,
	organization,

	openContextMenu: ({ account, cellContext, x, y }) => {
		setContextMenu({
			open: true,
			account,
			cellContext,
			x,
			y,
		});
	},
	closeContextMenu: () => {
		setContextMenu((prev) => ({ ...prev, open: false }));
	},
}), [
    setAuditTrailData,
    setIsAuditTrailOpen,
    handleOpenAuditTrail,
    props.isBCView,
    props.onOpenAccountManagement,
    props.editedCellIds,
	props.cellHasColor,
	organization,
]);

const tableComponents = useMemo(() => ({
    header: { cell: ResizableTitle },
    body: {
        row: EditableRow,
        cell: EditableCell,
    },
}), []);

const contextCellContext = contextMenu.cellContext;
const contextAccount = contextMenu.account;
const contextSharedItems = useSharedRowMenuItems({
	account: contextAccount,
	cellContext: contextCellContext,
	isBCView: props.isBCView,
	callbacks: {
		onOpenAccountManagement: props.onOpenAccountManagement,
		onShowWhereUsed: props.onShowWhereUsed,
	},
});

const contextMenuItems = [
	{
		key: 'audit-trail',
		label: 'Audit Trail',
		visible: contextCellContext != null && contextAccount != null,
		onClick: () => {
		const auditAccount = contextAccount.account ?? contextAccount;
		handleSetAuditTrailData(auditAccount.account_number, contextCellContext.period);
	},
},
{ key: 'div-1', divider: true, visible: contextSharedItems.length > 0 },
...contextSharedItems,
];

const headerMenuItems = [
    {
        key: 'hide-column',
        label: 'Hide column',
        onClick: () => {
            hideColumn(headerMenu.columnKey);
            setHeaderMenu((p) => ({ ...p, open: false }));
        },
    },
    { key: 'div-1', divider: true, visible: hidden.size > 0 },
    {
        key: 'show-all',
        label: `Show all columns (${hidden.size} hidden)`,
        visible: hidden.size > 0,
        onClick: () => { showAll(); setHeaderMenu((p) => ({ ...p, open: false })); },
    },
];

	return (
		<ScenarioTableContext.Provider value={tableContextValue}>
		<div className="scenario-table-toolbar">
				<ColumnSettings
					columns={columnToggles}
					widths={widths}
					onChange={setWidth}
					defaults={columnDefaults}
					resizableKeys={['account_number', 'name']}
					onToggleVisibility={(key, nextVisible) =>
						nextVisible ? showColumn(key) : hideColumn(key)
					}
					onShowAll={showAll}
					hiddenCount={hidden.size}
				/>
			</div>

			<div ref={wrapperRef}>
				<Table
				bordered
				components={tableComponents}
				columns={editableColumns}
				dataSource={keyedAccounts}
				rowClassName={getRowClassName}
				pagination={false}
				size="small"
				className="tableFooter scenario-table"
				rowKey="__rowKey"
				expandable={{
					expandedRowKeys,
					onExpandedRowsChange: setExpandedRowKeys,
					rowExpandable: (record) => record.children?.length > 0,
				}}
				scroll={effectiveHeight ? { x: 1300, y: effectiveHeight } : { x: 1300 }}
				virtual={!!effectiveHeight}
				sticky={
					 effectiveHeight
					? false
					: {
						offsetHeader: 0,
						getContainer: () =>
							document.getElementById(props.scrollContainerId) || window,
						}
				}
				/>
				<RowContextMenu
				open={contextMenu.open}
				onOpenChange={(open) => {
					setContextMenu((prev) => ({ ...prev, open }));
				}}
				items={contextMenuItems}
				getPopupContainer={() => document.body}
				>
				<div
					style={{
						position: 'fixed',
						left: contextMenu.x,
						top: contextMenu.y,
						width: 1,
						height: 1,
						pointerEvents: 'none',
						zIndex: 9999,
					}}
					/>
				</RowContextMenu>
				
				<RowContextMenu
				 	open={headerMenu.open}
					onOpenChange={(open) => setHeaderMenu((prev) => ({ ...prev, open }))}
					items={headerMenuItems}
					getPopupContainer={() => document.body}
					>
						<div
						style={{
						position: 'fixed',
						left: headerMenu.x,
						top: headerMenu.y,
						width: 1,
						height: 1,
						pointerEvents: 'none',
						zIndex: 9999,
					}}
				/>
				</RowContextMenu>
		</div>
				
				{auditTrailData != null ? (
					<AuditTrail
					expandedPeriods={expandedPeriods}
					periods={periods}
					auditTrailData={auditTrailData}
					handleSetAuditTrailData={handleSetAuditTrailData}
					handleSetPreviousAuditTrailData={handleSetPreviousAuditTrailData}
					accountStructure={props.structure}
					scenario={props.scenario}
					businessCase={props.businessCase}
					isAuditTrailOpen={isAuditTrailOpen}
					setIsAuditTrailOpen={setIsAuditTrailOpen}
				/>
			) : null}
			 </ScenarioTableContext.Provider>
	);
};

export default ScenarioTable;
